/**
 * The Discord tab's team (or Hiders/Seekers) voice channels and roles: made
 * by SWTORBIT for the game and deleted at the end, pre-existing ones of the
 * server's that it only links (never renames or deletes), or none.
 *
 * - Hide and Seek: the Hiders and Seekers channels.
 * - Hide and Seek Teams: each team's channel and role.
 * - FP Race / Trailblazers: a pool of channels (and roles) handed to the teams
 *   in team order, and any team's own choice in place of the pool.
 */

import { Dropdown, DropdownItem, Segmented } from '@swtorbit/ui/react';
import {
  GameType,
  HNST_TEAM_KEYS,
  HNST_TEAMS,
  getDiscordChannelSource,
  getDiscordRoleSource,
  type DiscordChannel,
  type DiscordChannelSource,
  type DiscordGameSettingsPatch,
  type DiscordRole,
  type DiscordRoleSource,
  type DiscordTeamLink,
  type Game,
} from '@swtorbit/shared-socket-events';

type SetGameSettings = React.Dispatch<React.SetStateAction<DiscordGameSettingsPatch>>;

// ============================================================================
// Pickers
// ============================================================================

/** A role's look: its icon image, its emoji, or a swatch of its colour. */
function RoleIcon({ role }: { role: DiscordRole }) {
  if (role.iconUrl) return <img src={role.iconUrl} alt="" />;
  if (role.unicodeEmoji) return <span aria-hidden="true">{role.unicodeEmoji}</span>;
  // A role's colour is the server's own data, not one of ours: drawn as an
  // SVG fill rather than a style.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill={role.colorHex ?? 'currentColor'} />
    </svg>
  );
}

interface DiscordRolePickerProps {
  roles: DiscordRole[];
  value: string | undefined | null;
  onChange: (roleId: string | null) => void;
  /** The "no role" choice. */
  noneLabel?: string;
  /** Only offer roles the bot can grant and revoke (team roles). Officer roles needn't be. */
  requireAssignable?: boolean;
}

/** Choose a server role, shown with its icon or colour. */
export function DiscordRolePicker({ roles, value, onChange, noneLabel = '-- Select role --', requireAssignable = true }: DiscordRolePickerProps) {
  const selected = roles.find((r) => r.id === value);
  return (
    <Dropdown
      block
      trigger={{ variant: 'outline', hue: 'slate', size: 'sm' }}
      label={
        selected ? (
          <>
            <span className="dropdown__icon"><RoleIcon role={selected} /></span>
            {selected.name}
          </>
        ) : (
          noneLabel
        )
      }
    >
      <DropdownItem selected={!selected} onClick={() => onChange(null)}>{noneLabel}</DropdownItem>
      {roles.map((role) => {
        const usable = role.assignable || !requireAssignable;
        return (
          <DropdownItem
            key={role.id}
            icon={<RoleIcon role={role} />}
            selected={role.id === value}
            disabled={!usable}
            meta={usable ? undefined : "Bot can't assign"}
            onClick={() => onChange(role.id)}
          >
            {role.name}
          </DropdownItem>
        );
      })}
    </Dropdown>
  );
}

interface VoiceChannelSelectProps {
  id: string;
  channels: DiscordChannel[];
  value: string | undefined | null;
  onChange: (channelId: string | null) => void;
  placeholder?: string;
  /** Extra choices before the channels, e.g. "From the pool". */
  extraOptions?: Array<{ value: string; label: string }>;
}

/** Choose a voice channel; ones the bot can't move members in say what it lacks. */
export function VoiceChannelSelect({ id, channels, value, onChange, placeholder = '-- Select channel --', extraOptions = [] }: VoiceChannelSelectProps) {
  return (
    <select className="select" id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{placeholder}</option>
      {extraOptions.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
      {channels.map((channel) => (
        <option key={channel.id} value={channel.id}>
          {channel.name}
          {channel.missingPermissions?.length ? ` (bot lacks ${channel.missingPermissions.join(', ')})` : ''}
        </option>
      ))}
    </select>
  );
}

// ============================================================================
// Sources
// ============================================================================

const CHANNEL_SOURCE_HINTS: Record<DiscordChannelSource, string> = {
  create: 'SWTORBIT makes private channels for the game and deletes them when it ends.',
  existing: "The server's own channels: SWTORBIT moves people in and out but never renames or deletes them.",
  off: 'No channels: nobody is moved between them.',
};

const ROLE_SOURCE_HINTS: Record<DiscordRoleSource, string> = {
  create: "SWTORBIT makes a role for each team (name and colour) and deletes it when the game ends.",
  existing: "The server's own roles: SWTORBIT grants and revokes them but never renames, recolours or deletes them.",
};

const isTeamGame = (gameType: GameType) => gameType !== GameType.HIDE_AND_SEEK;

// ============================================================================
// Component
// ============================================================================

interface TeamChannelSettingsProps {
  game: Game;
  settings: DiscordGameSettingsPatch;
  setSettings: SetGameSettings;
  voiceChannels: DiscordChannel[];
  categories: DiscordChannel[];
  roles: DiscordRole[];
}

export function TeamChannelSettings({ game, settings, setSettings, voiceChannels, categories, roles }: TeamChannelSettingsProps) {
  const teamGame = isTeamGame(game.gameType);
  const channelSource = getDiscordChannelSource(game.gameType, nonNull(settings));
  const roleSource = getDiscordRoleSource(nonNull(settings));

  const setChannelSource = (source: DiscordChannelSource) =>
    setSettings((prev) => ({
      ...prev,
      channelSource: source,
      // Hide and Seek's old toggle, kept in step for games read before the choice.
      ...(game.gameType === GameType.HIDE_AND_SEEK ? { autoCreateChannels: source === 'create' } : {}),
    }));

  return (
    <div className="d-flex flex-column gap-sm">
      <div className="field">
        <span className="field__label">{teamGame ? 'Team voice channels' : 'Hiders and Seekers voice channels'}</span>
        <Segmented
          size="sm"
          aria-label="Where the voice channels come from"
          value={channelSource}
          onChange={setChannelSource}
          options={[
            { value: 'create', label: 'SWTORBIT creates them' },
            { value: 'existing', label: 'Already exist' },
            { value: 'off', label: 'Off' },
          ]}
        />
        <span className="field__hint">{CHANNEL_SOURCE_HINTS[channelSource]}</span>
      </div>

      {channelSource === 'create' && (
        <div className="field sub-option">
          <label htmlFor="discord-channel-category">Category for the channels (optional):</label>
          <select
            className="select"
            id="discord-channel-category"
            value={settings.channelCategoryId ?? ''}
            onChange={(e) => setSettings((prev) => ({ ...prev, channelCategoryId: e.target.value || null }))}
          >
            <option value="">-- Top of the server --</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>
      )}

      {teamGame && channelSource !== 'off' && (
        <div className="field">
          <span className="field__label">Team roles</span>
          <Segmented
            size="sm"
            aria-label="Where the team roles come from"
            value={roleSource}
            onChange={(source) => setSettings((prev) => ({ ...prev, roleSource: source }))}
            options={[
              { value: 'create', label: 'SWTORBIT creates them' },
              { value: 'existing', label: 'Already exist' },
            ]}
          />
          <span className="field__hint">{ROLE_SOURCE_HINTS[roleSource]}</span>
          {roleSource === 'existing' && (
            <span className="field__hint">
              Greyed-out roles sit above the SWTORBIT bot&apos;s role or belong to an integration: move the bot&apos;s role above them to use them.
            </span>
          )}
          {channelSource === 'existing' && roleSource === 'create' && (
            <span className="field__hint">
              SWTORBIT doesn&apos;t change your channels&apos; permissions, so make sure the new roles can join them.
            </span>
          )}
        </div>
      )}

      {channelSource !== 'off' && (
        <LinkedSlots
          game={game}
          settings={settings}
          setSettings={setSettings}
          voiceChannels={voiceChannels}
          roles={roles}
          channelsLinked={channelSource === 'existing'}
          rolesLinked={teamGame && roleSource === 'existing'}
        />
      )}
    </div>
  );
}

/** The settings without the patch's "clear" nulls, for reading the sources. */
function nonNull(settings: DiscordGameSettingsPatch) {
  return {
    channelSource: settings.channelSource ?? undefined,
    roleSource: settings.roleSource ?? undefined,
    autoCreateChannels: settings.autoCreateChannels ?? undefined,
  };
}

// ============================================================================
// The linked channels and roles
// ============================================================================

interface LinkedSlotsProps {
  game: Game;
  settings: DiscordGameSettingsPatch;
  setSettings: SetGameSettings;
  voiceChannels: DiscordChannel[];
  roles: DiscordRole[];
  channelsLinked: boolean;
  rolesLinked: boolean;
}

function LinkedSlots(props: LinkedSlotsProps) {
  const { game, settings, setSettings, voiceChannels, channelsLinked, rolesLinked } = props;
  if (!channelsLinked && !rolesLinked) return null;

  switch (game.gameType) {
    case GameType.HIDE_AND_SEEK:
      return (
        <div className="sub-options">
          <div className="field sub-option">
            <label htmlFor="hiders-channel">Hiders Voice Channel:</label>
            <VoiceChannelSelect
              id="hiders-channel"
              channels={voiceChannels}
              value={settings.hnsHidersVoiceChannelId}
              onChange={(id) => setSettings((prev) => ({ ...prev, hnsHidersVoiceChannelId: id }))}
            />
          </div>
          <div className="field sub-option">
            <label htmlFor="seekers-channel">Seekers Voice Channel:</label>
            <VoiceChannelSelect
              id="seekers-channel"
              channels={voiceChannels}
              value={settings.hnsSeekersVoiceChannelId}
              onChange={(id) => setSettings((prev) => ({ ...prev, hnsSeekersVoiceChannelId: id }))}
            />
          </div>
        </div>
      );
    case GameType.HIDE_AND_SEEK_TEAMS:
      return <HnstTeamLinks {...props} />;
    case GameType.FP_RACE:
    case GameType.TRAILBLAZERS:
      return (
        <>
          <TeamChannelPool {...props} />
          <TeamOverrides {...props} />
        </>
      );
  }
}

/** The channel and/or role picker for one linked slot. */
function LinkPickers({
  id,
  link,
  onChange,
  voiceChannels,
  roles,
  channelsLinked,
  rolesLinked,
}: {
  id: string;
  link: DiscordTeamLink | undefined;
  onChange: (link: DiscordTeamLink) => void;
  voiceChannels: DiscordChannel[];
  roles: DiscordRole[];
  channelsLinked: boolean;
  rolesLinked: boolean;
}) {
  return (
    <>
      {channelsLinked && (
        <VoiceChannelSelect
          id={`${id}-channel`}
          channels={voiceChannels}
          value={link?.channelId}
          onChange={(channelId) => onChange({ ...link, channelId: channelId ?? undefined })}
        />
      )}
      {rolesLinked && (
        <DiscordRolePicker
          roles={roles}
          value={link?.roleId}
          onChange={(roleId) => onChange({ ...link, roleId: roleId ?? undefined })}
        />
      )}
    </>
  );
}

function HnstTeamLinks({ settings, setSettings, voiceChannels, roles, channelsLinked, rolesLinked }: LinkedSlotsProps) {
  const links = settings.hnstTeamLinks ?? {};
  return (
    <div className="sub-options">
      {HNST_TEAM_KEYS.map((team) => (
        <div key={team} className="field sub-option">
          <span className="field__label">{HNST_TEAMS[team].name}:</span>
          <div className="d-flex flex-column gap-2xs">
            <LinkPickers
              id={`hnst-${team}`}
              link={links[team]}
              onChange={(link) => setSettings((prev) => ({ ...prev, hnstTeamLinks: { ...prev.hnstTeamLinks, [team]: link } }))}
              voiceChannels={voiceChannels}
              roles={roles}
              channelsLinked={channelsLinked}
              rolesLinked={rolesLinked}
            />
          </div>
        </div>
      ))}
      {rolesLinked && (
        <span className="field__hint">A team left without a role gets one SWTORBIT makes.</span>
      )}
    </div>
  );
}

function TeamChannelPool({ settings, setSettings, voiceChannels, roles, channelsLinked, rolesLinked }: LinkedSlotsProps) {
  const pool = settings.teamChannelPool ?? [];
  const setPool = (next: DiscordTeamLink[]) => setSettings((prev) => ({ ...prev, teamChannelPool: next }));
  const what = channelsLinked ? 'channel' : 'role';

  return (
    <div className="field sub-options">
      <span className="field__label">Team {what}s, in team order:</span>
      {pool.map((entry, i) => (
        <div key={i} className="d-flex align-items-center gap-xs">
          <span className="text-slate fs-sm">{i + 1}.</span>
          <div className="d-flex flex-column gap-2xs flex-grow-1">
            <LinkPickers
              id={`pool-${i}`}
              link={entry}
              onChange={(link) => setPool(pool.map((e, j) => (j === i ? link : e)))}
              voiceChannels={voiceChannels}
              roles={roles}
              channelsLinked={channelsLinked}
              rolesLinked={rolesLinked}
            />
          </div>
          <button
            type="button"
            className="btn btn--sm btn--ghost btn--slate"
            disabled={i === 0}
            aria-label={`Move ${what} ${i + 1} up`}
            onClick={() => setPool(pool.map((e, j) => (j === i - 1 ? pool[i] : j === i ? pool[i - 1] : e)))}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn--sm btn--ghost btn--red"
            aria-label={`Remove ${what} ${i + 1}`}
            onClick={() => setPool(pool.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn--sm btn--outline btn--slate" onClick={() => setPool([...pool, {}])}>
          + Add a team {what}
        </button>
      </div>
      <span className="field__hint">
        Teams take these in order and keep theirs as teams come and go. Teams beyond the list get {what}s SWTORBIT makes.
      </span>
    </div>
  );
}

const POOL = '';
const BOT = '__bot';

/** Each team's own choice in place of the pool (FP Race, Trailblazers), once the teams exist. */
function TeamOverrides({ game, settings, setSettings, voiceChannels, roles, channelsLinked, rolesLinked }: LinkedSlotsProps) {
  const teams = game.gameType === GameType.FP_RACE ? game.fprTeams ?? [] : game.trlTeams ?? [];
  if (teams.length === 0) return null;
  const overrides = settings.teamLinkOverrides ?? {};

  const setOverride = (teamId: string, link: DiscordTeamLink | undefined) =>
    setSettings((prev) => {
      const next = { ...prev.teamLinkOverrides };
      if (link) next[teamId] = link;
      else delete next[teamId];
      return { ...prev, teamLinkOverrides: next };
    });

  return (
    <div className="field sub-options">
      <span className="field__label">Teams:</span>
      {teams.map((team) => {
        const override = overrides[team.id];
        // Which the team uses: the pool, SWTORBIT's own, or a channel of its own.
        const choice = !override ? POOL : override.channelId ?? BOT;
        return (
          <div key={team.id} className="field sub-option">
            <label htmlFor={`team-${team.id}-channel`}>{team.name}:</label>
            <div className="d-flex flex-column gap-2xs">
              {channelsLinked ? (
                <VoiceChannelSelect
                  id={`team-${team.id}-channel`}
                  channels={voiceChannels}
                  value={choice}
                  placeholder="From the list above"
                  extraOptions={[{ value: BOT, label: 'SWTORBIT makes its own' }]}
                  onChange={(value) => {
                    if (!value) setOverride(team.id, undefined);
                    else if (value === BOT) setOverride(team.id, { roleId: override?.roleId });
                    else setOverride(team.id, { channelId: value, roleId: override?.roleId });
                  }}
                />
              ) : null}
              {rolesLinked && (override || !channelsLinked) && (
                <DiscordRolePicker
                  roles={roles}
                  value={override?.roleId}
                  noneLabel={channelsLinked ? 'SWTORBIT makes its role' : 'From the list above'}
                  onChange={(roleId) =>
                    setOverride(team.id, roleId || channelsLinked ? { ...override, roleId: roleId ?? undefined } : undefined)
                  }
                />
              )}
            </div>
          </div>
        );
      })}
      <span className="field__hint">
        A team&apos;s own choice replaces the list for it; saving moves a team already playing onto it.
      </span>
    </div>
  );
}
